// Modal Configuration - Control centralizado para TODOS los modales
// Cambiar aquí afecta TODA la aplicación

export const MODAL_STYLES = {
  // Fondos y efectos - Control centralizado del blur
  backdrop: {
    blur: 'backdrop-blur-sm bg-black/30',      // Estándar con blur
    heavy: 'backdrop-blur-md bg-black/40',     // Blur más fuerte
    light: 'backdrop-blur-sm bg-black/20',     // Blur más suave
    dark: 'bg-black/50',                       // Sin blur, oscuro
    none: 'bg-transparent'                     // Sin fondo
  },
  
  // Tamaños predefinidos
  sizes: {
    xs: 'max-w-xs',      // 320px
    sm: 'max-w-sm',      // 384px  
    md: 'max-w-md',      // 448px
    lg: 'max-w-lg',      // 512px
    xl: 'max-w-xl',      // 576px
    '2xl': 'max-w-2xl',  // 672px
    '3xl': 'max-w-3xl',  // 768px
    '4xl': 'max-w-4xl',  // 896px
    full: 'w-full h-full' // Pantalla completa
  },
  
  // Z-index unificado
  zIndex: {
    modal: 'z-50',        // Modales estándar
    overlay: 'z-50',      // Overlays
    dropdown: 'z-40',     // Dropdowns dentro de modales
    tooltip: 'z-60'       // Tooltips sobre modales
  },
  
  // Animaciones consistentes
  animations: {
    // Backdrop fade
    backdrop: {
      enter: 'transition-all duration-300 ease-out',
      enterFrom: 'opacity-0',
      enterTo: 'opacity-100',
      leave: 'transition-all duration-200 ease-in',
      leaveFrom: 'opacity-100',
      leaveTo: 'opacity-0'
    },
    
    // Modal content scale + fade
    content: {
      enter: 'transition-all duration-300 ease-out',
      enterFrom: 'opacity-0 scale-95 translate-y-4',
      enterTo: 'opacity-100 scale-100 translate-y-0',
      leave: 'transition-all duration-200 ease-in', 
      leaveFrom: 'opacity-100 scale-100 translate-y-0',
      leaveTo: 'opacity-0 scale-95 translate-y-4'
    }
  },
  
  // Estilos base del contenedor
  container: {
    base: 'bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700',
    padding: 'p-6',
    spacing: 'space-y-4'
  },
  
  // Headers estandarizados
  header: {
    base: 'flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700',
    title: 'text-lg font-semibold text-gray-900 dark:text-white',
    closeButton: 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700'
  },
  
  // Botones estandarizados
  buttons: {
    primary: 'px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
    secondary: 'px-4 py-2 bg-gray-200 text-gray-900 dark:bg-gray-600 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors',
    danger: 'px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
    ghost: 'px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors'
  }
} as const;

// Configuración de comportamiento
export const MODAL_BEHAVIOR = {
  // Keyboard handling
  keyboard: {
    closeOnEscape: true,
    trapFocus: true
  },
  
  // Mouse behavior  
  mouse: {
    closeOnBackdrop: true,
    preventBackdropClose: false
  },
  
  // Responsive behavior
  responsive: {
    fullScreenOnMobile: true,
    centerOnDesktop: true
  },
  
  //  Accessibility
  accessibility: {
    autoFocus: true,
    returnFocus: true,
    ariaModal: true,
    role: 'dialog'
  }
} as const;

// Tipos TypeScript para autocompletado
export type BackdropType = keyof typeof MODAL_STYLES.backdrop;
export type ModalSize = keyof typeof MODAL_STYLES.sizes;
export type ButtonVariant = keyof typeof MODAL_STYLES.buttons;

//  Configuración global que se puede cambiar en runtime
export class ModalConfig {
  private static _defaultBackdrop: BackdropType = 'blur';
  private static _defaultSize: ModalSize = 'md';
  
  // Getters
  static get defaultBackdrop() { return this._defaultBackdrop; }
  static get defaultSize() { return this._defaultSize; }
  
  // Setters para cambiar configuración global
  static setDefaultBackdrop(backdrop: BackdropType) {
    this._defaultBackdrop = backdrop;
  }
  
  static setDefaultSize(size: ModalSize) {
    this._defaultSize = size;
  }
  
  // Reset a valores por defecto
  static reset() {
    this._defaultBackdrop = 'blur';
    this._defaultSize = 'md';
  }
}

// Helper functions
export const getBackdropClasses = (type: BackdropType = ModalConfig.defaultBackdrop) => {
  return MODAL_STYLES.backdrop[type];
};

export const getSizeClasses = (size: ModalSize = ModalConfig.defaultSize) => {
  return MODAL_STYLES.sizes[size];
};

export const getButtonClasses = (variant: ButtonVariant = 'primary') => {
  return MODAL_STYLES.buttons[variant];
};